<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import { useNotificationSettingsStore } from '../../stores/notificationSettingsStore'
import type { TNotificationChannel } from '../../types/notificationSettings.types'
import type { TNotificationMessageType } from '../../types/notification.types'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

/**
 * Категории и их типы приходят с бека (preset). Каждая категория рендерится
 * отдельной карточкой; названия каналов, групп и типов берутся напрямую из ответа.
 *
 * В колонках — каналы, которые бек прислал в preset (валидные для пользователя).
 * Ячейка пустая если бек не прислал пару (channel, type) — у пользователя пара недоступна.
 *
 * Чекбокс критичного типа дизейблится когда оставлен последний активный канал —
 * чтобы пользователь не отключил всю доставку критичных уведомлений целиком.
 *
 * Кнопка «Сохранить» одна на все категории, отправляет полный preset на бек.
 */

const store = useNotificationSettingsStore()
const feedback = useFeedbackStore()
const { channels, categories, isDirty, isSaving, hasInvalidCritical } = storeToRefs(store)
const { mobile } = useDisplay()

function isCheckboxDisabled(channel: TNotificationChannel, type: TNotificationMessageType, current: boolean): boolean {
  if (current !== true) return false

  return !store.canDisable(channel, type)
}

function setCell(channel: TNotificationChannel, type: TNotificationMessageType, value: boolean): void {
  store.setSetting(channel, type, value)
}

async function onSave(): Promise<void> {
  try {
    await store.saveSettings()
    feedback.showToast({ type: 'success', message: 'Настройки сохранены' })
  } catch (error) {
    feedback.showToast({ type: 'error', message: 'Не удалось сохранить настройки' })
    console.error('[NotificationMatrixTable] saveSettings failed', error)
  }
}
</script>

<template>
  <VCard
    elevation="0"
    class="matrix-card"
  >
    <template v-for="cat in categories" :key="cat.name">
      <!-- Заголовок-секция категории с подсветкой -->
      <div class="matrix-card__section-title">
        {{ cat.name }}
      </div>

      <VTable density="comfortable" class="matrix-table">
        <thead>
          <tr>
            <th class="matrix-table__col-type text-wrap">
              ТИП
            </th>
            <th
              v-for="ch in channels"
              :key="ch.label"
              class="text-center text-wrap"
            >
              {{ ch.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in cat.types"
            :key="row.type"
          >
            <td class="matrix-table__col-type text-wrap">
              {{ row.name }}
            </td>
            <td
              v-for="ch in channels"
              :key="`${row.type}-${ch.label}`"
              class="text-center"
            >
              <VCheckbox
                v-if="ch.label in row.channels"
                :model-value="row.channels[ch.label]"
                :disabled="isCheckboxDisabled(ch.label, row.type, row.channels[ch.label])"
                hide-details
                density="compact"
                class="d-inline-flex"
                @update:model-value="(value) => setCell(ch.label, row.type, Boolean(value))"
              />
              <span v-else class="text-disabled">—</span>
            </td>
          </tr>
        </tbody>
      </VTable>
    </template>

    <!-- Кнопка сохранения внутри карточки, одна на обе категории -->
    <div class="matrix-card__footer">
      <VBtn
        color="primary"
        :block="mobile"
        :disabled="!isDirty || hasInvalidCritical"
        :loading="isSaving"
        @click="onSave"
      >
        Сохранить
      </VBtn>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.matrix-card {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 8px;
  overflow: hidden;

  &__section-title {
    padding: 16px 20px;
    background: rgba(var(--v-theme-primary), 0.04);
    font-size: 16px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.87);
  }

  &__footer {
    display: flex;
    padding: 16px 20px;
  }
}

.matrix-table {
  &__col-type {
    inline-size: 40%;
    position: sticky;
    left: 0;
    z-index: 1;
    background: rgb(var(--v-theme-surface)) !important;
  }

  :deep(.v-table__wrapper table thead tr th) {
    --v-overlay-scrim-opacity: 1;

    background: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    font-size: 12px;
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &.matrix-table__col-type {
      position: sticky;
      left: 0;
      z-index: 2;
      background: rgb(var(--v-theme-primary-100)) !important;
    }
  }
}
</style>
